import { css, cx } from "styled-system/css";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { es } from "date-fns/locale/es";
import { AdminPageShell } from "@/components/admin/admin-page-shell";
import { cf } from "@/components/brand/sfturnos-styles";
import { Button } from "@/components/ui/button";
import { AccountStatusBadge, type RealStatus } from "@/components/admin/account-status-badge";

// ─── Types ────────────────────────────────────────────────────────────────────

type PlanKey = "FREE" | "PRO" | "ENTERPRISE";

type PlanPricing = {
  id: string;
  plan: PlanKey;
  monthlyPrice: number;
  annualPrice: number;
  extraAgendaMonthly: number;
  extraAgendaAnnual: number;
};

type SubscriptionRow = {
  id: string;
  name: string;
  slug: string;
  phone: string | null;
  email: string | null;
  plan: PlanKey;
  planExpiresAt: string | null;
  pendingPlanPayment: boolean;
  agendasAllowed: number | null;
  isActive: boolean;
  category: string | null;
  specialty: string | null;
  mpConnected: boolean;
  googleConnected: boolean;
  appointmentsCount: number;
  createdAt: string;
  billingCycle: "monthly" | "annual";
  agendasCount: number;
  priceOverride: number | null;
  subscriptionStatus: "active" | "pending" | "cancelled";
  currentPeriodEnd: string | null;
  monthlyRate: number;
  trialDaysLeft: number | null;
  realStatus: RealStatus;
};

type AdminData = {
  professionals: SubscriptionRow[];
  mrr: {
    real: number;
    potential: number;
    byPlanReal: Record<string, number>;
  };
};

type PricingData = {
  pricing: PlanPricing[];
  annualBillingEnabled: boolean;
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatARS(n: number) {
  return new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
    maximumFractionDigits: 0,
  }).format(n);
}

const PLAN_LABEL: Record<PlanKey, string> = {
  FREE: "Free",
  PRO: "Pro",
  ENTERPRISE: "Enterprise",
};

const PLAN_COLORS: Record<PlanKey, string> = {
  FREE: "#64748b",
  PRO: "#4338CA",
  ENTERPRISE: "#7c3aed",
};

// ─── Small components ─────────────────────────────────────────────────────────

function MrrCard({
  label,
  value,
  sub,
  accent,
}: {
  label: string;
  value: string;
  sub?: string;
  accent?: string;
}) {
  return (
    <div
      className={cx(cf.dashSurface, css({ p: "5", borderTopWidth: "3px" }))}
      style={{ borderTopColor: accent ?? "#e2e8f0" }}
    >
      <p className={css({ fontSize: "xs", color: "#64748b", mb: "1", fontWeight: "medium" })}>
        {label}
      </p>
      <p
        className={css({
          fontSize: "2xl",
          fontWeight: "bold",
          letterSpacing: "tight",
          color: "#0f172a",
        })}
      >
        {value}
      </p>
      {sub ? (
        <p className={css({ fontSize: "xs", color: "#94a3b8", mt: "1" })}>{sub}</p>
      ) : null}
    </div>
  );
}

function CountCard({
  label,
  count,
  dot,
  sub,
}: {
  label: string;
  count: number;
  dot: string;
  sub?: string;
}) {
  return (
    <div className={cx(cf.dashSurface, css({ p: "5", display: "flex", flexDir: "column", gap: "1" }))}>
      <div className={css({ display: "flex", alignItems: "center", gap: "2", mb: "0.5" })}>
        <span
          className={css({ w: "2", h: "2", rounded: "full", flexShrink: 0 })}
          style={{ background: dot }}
        />
        <p className={css({ fontSize: "xs", color: "#64748b", fontWeight: "medium" })}>{label}</p>
      </div>
      <p className={css({ fontSize: "2xl", fontWeight: "bold", color: "#0f172a", letterSpacing: "tight" })}>
        {count}
      </p>
      {sub ? <p className={css({ fontSize: "xs", color: "#94a3b8" })}>{sub}</p> : null}
    </div>
  );
}

// ─── Plan Price Editor ────────────────────────────────────────────────────────

function PlanPriceEditor({
  pricing,
  onSaved,
}: {
  pricing: PlanPricing[];
  onSaved: () => void;
}) {
  const [editing, setEditing] = useState<string | null>(null);
  const [form, setForm] = useState<Partial<PlanPricing>>({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ text: string; ok: boolean } | null>(null);

  function startEdit(row: PlanPricing) {
    setEditing(row.plan);
    setForm({ ...row });
    setMsg(null);
  }

  async function save() {
    if (!editing || !form) return;
    setBusy(true);
    setMsg(null);
    const r = await fetch("/api/admin/plan-pricing", {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ plan: editing, ...form }),
    });
    setBusy(false);
    if (!r.ok) {
      setMsg({ text: "No se pudo guardar.", ok: false });
      return;
    }
    setMsg({ text: "Guardado.", ok: true });
    setEditing(null);
    onSaved();
  }

  const sorted = [...pricing].sort(
    (a, b) =>
      (["FREE", "PRO", "ENTERPRISE"] as const).indexOf(a.plan) -
      (["FREE", "PRO", "ENTERPRISE"] as const).indexOf(b.plan),
  );

  return (
    <div
      className={css({
        display: "grid",
        gridTemplateColumns: { base: "1fr", md: "repeat(3, 1fr)" },
        gap: "4",
      })}
    >
      {sorted.map((row) => {
        const isEditing = editing === row.plan;
        const color = PLAN_COLORS[row.plan] ?? "#0f172a";
        return (
          <div
            key={row.plan}
            className={cx(
              cf.dashSurface,
              css({ p: "5", borderTopWidth: "3px" }),
            )}
            style={{ borderTopColor: color }}
          >
            <p
              className={css({
                fontSize: "xs",
                fontWeight: "bold",
                mb: "3",
                letterSpacing: "wide",
              })}
              style={{ color }}
            >
              {PLAN_LABEL[row.plan]}
            </p>

            {isEditing ? (
              <div className={css({ display: "grid", gap: "3" })}>
                {row.plan !== "FREE" && (
                  <>
                    <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
                      <span className={css({ fontSize: "xs", color: "#64748b" })}>
                        Precio mensual (ARS)
                      </span>
                      <input
                        type="number"
                        className={cf.input}
                        value={form.monthlyPrice ?? 0}
                        onChange={(e) =>
                          setForm({ ...form, monthlyPrice: Number(e.target.value) })
                        }
                      />
                    </label>
                    <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
                      <span className={css({ fontSize: "xs", color: "#64748b" })}>
                        Precio anual / mes (ARS)
                      </span>
                      <input
                        type="number"
                        className={cf.input}
                        value={form.annualPrice ?? 0}
                        onChange={(e) =>
                          setForm({ ...form, annualPrice: Number(e.target.value) })
                        }
                      />
                    </label>
                  </>
                )}
                {row.plan === "ENTERPRISE" && (
                  <>
                    <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
                      <span className={css({ fontSize: "xs", color: "#64748b" })}>
                        Agenda extra mensual (ARS)
                      </span>
                      <input
                        type="number"
                        className={cf.input}
                        value={form.extraAgendaMonthly ?? 0}
                        onChange={(e) =>
                          setForm({ ...form, extraAgendaMonthly: Number(e.target.value) })
                        }
                      />
                    </label>
                    <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
                      <span className={css({ fontSize: "xs", color: "#64748b" })}>
                        Agenda extra anual / mes (ARS)
                      </span>
                      <input
                        type="number"
                        className={cf.input}
                        value={form.extraAgendaAnnual ?? 0}
                        onChange={(e) =>
                          setForm({ ...form, extraAgendaAnnual: Number(e.target.value) })
                        }
                      />
                    </label>
                  </>
                )}
                {msg && (
                  <p
                    className={css({ fontSize: "xs" })}
                    style={{ color: msg.ok ? "#15803d" : "#dc2626" }}
                  >
                    {msg.text}
                  </p>
                )}
                <div className={css({ display: "flex", gap: "2" })}>
                  <Button
                    type="button"
                    variant="primary"
                    onClick={() => void save()}
                    disabled={busy}
                  >
                    {busy ? "Guardando…" : "Guardar"}
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      setEditing(null);
                      setMsg(null);
                    }}
                  >
                    Cancelar
                  </Button>
                </div>
              </div>
            ) : (
              <div className={css({ display: "grid", gap: "2" })}>
                {row.plan === "FREE" ? (
                  <p className={css({ fontSize: "2xl", fontWeight: "bold", color: "#0f172a" })}>
                    Gratis
                  </p>
                ) : (
                  <>
                    <div>
                      <p className={css({ fontSize: "xs", color: "#94a3b8" })}>Mensual</p>
                      <p className={css({ fontSize: "xl", fontWeight: "bold", color: "#0f172a" })}>
                        {formatARS(row.monthlyPrice)}
                        <span className={css({ fontSize: "sm", fontWeight: "normal", color: "#94a3b8" })}>
                          /mes
                        </span>
                      </p>
                    </div>
                    <div>
                      <p className={css({ fontSize: "xs", color: "#94a3b8" })}>Anual (por mes)</p>
                      <p className={css({ fontSize: "lg", fontWeight: "semibold", color: "#0f172a" })}>
                        {formatARS(row.annualPrice)}
                        <span className={css({ fontSize: "sm", fontWeight: "normal", color: "#94a3b8" })}>
                          /mes
                        </span>
                      </p>
                    </div>
                    {row.plan === "ENTERPRISE" && (
                      <>
                        <div>
                          <p className={css({ fontSize: "xs", color: "#94a3b8" })}>
                            Agenda extra (mensual)
                          </p>
                          <p className={css({ fontSize: "sm", fontWeight: "semibold", color: "#0f172a" })}>
                            {formatARS(row.extraAgendaMonthly)}
                          </p>
                        </div>
                        <div>
                          <p className={css({ fontSize: "xs", color: "#94a3b8" })}>
                            Agenda extra (anual)
                          </p>
                          <p className={css({ fontSize: "sm", fontWeight: "semibold", color: "#0f172a" })}>
                            {formatARS(row.extraAgendaAnnual)}
                          </p>
                        </div>
                      </>
                    )}
                  </>
                )}
                {row.plan !== "FREE" && (
                  <Button type="button" variant="secondary" onClick={() => startEdit(row)}>
                    Editar precios
                  </Button>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Filter pill ──────────────────────────────────────────────────────────────

function FilterPill({
  active,
  count,
  label,
  onClick,
}: {
  active: boolean;
  count?: number;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={css({
        px: "3",
        py: "1.5",
        rounded: "full",
        fontSize: "xs",
        fontWeight: "semibold",
        cursor: "pointer",
        borderWidth: "1px",
        transition: "all 0.15s ease",
        whiteSpace: "nowrap",
        bg: active ? "#0f172a" : "white",
        color: active ? "white" : "#64748b",
        borderColor: active ? "#0f172a" : "#e2e8f0",
        _hover: { borderColor: "#0f172a" },
      })}
    >
      {label}
      {count !== undefined ? (
        <span
          className={css({ ml: "1.5", opacity: 0.7 })}
        >
          {count}
        </span>
      ) : null}
    </button>
  );
}

// ─── Inline subscription editor ───────────────────────────────────────────────

type EditForm = {
  plan: string;
  billingCycle: string;
  agendasCount: number;
  priceOverride: string;
  pendingPlanPayment: boolean;
  note: string;
};

function SubscriptionEditor({
  row,
  onSaved,
  onCancel,
}: {
  row: SubscriptionRow;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const [form, setForm] = useState<EditForm>({
    plan: row.plan,
    billingCycle: row.billingCycle,
    agendasCount: row.agendasCount,
    priceOverride: row.priceOverride != null ? String(row.priceOverride) : "",
    pendingPlanPayment: row.pendingPlanPayment,
    note: "",
  });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ text: string; ok: boolean } | null>(null);

  async function save() {
    setBusy(true);
    setMsg(null);
    const r = await fetch(`/api/admin/subscriptions/${row.id}`, {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        plan: form.plan,
        billingCycle: form.billingCycle,
        agendasCount: form.agendasCount,
        priceOverride:
          form.priceOverride.trim() === "" ? null : Number(form.priceOverride),
        pendingPlanPayment: form.pendingPlanPayment,
        note: form.note || undefined,
      }),
    });
    setBusy(false);
    if (!r.ok) {
      setMsg({ text: "No se pudo guardar.", ok: false });
      return;
    }
    setMsg({ text: "Guardado.", ok: true });
    setTimeout(onSaved, 400);
  }

  return (
    <div
      className={css({
        mt: "3",
        pt: "3",
        borderTopWidth: "1px",
        borderColor: "#f1f5f9",
        display: "grid",
        gridTemplateColumns: { base: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
        gap: "3",
      })}
    >
      <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
        <span className={css({ fontSize: "xs", color: "#64748b" })}>Plan</span>
        <select
          className={cf.input}
          value={form.plan}
          onChange={(e) => setForm({ ...form, plan: e.target.value })}
        >
          <option value="FREE">Free</option>
          <option value="PRO">Pro</option>
          <option value="ENTERPRISE">Enterprise</option>
        </select>
      </label>
      <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
        <span className={css({ fontSize: "xs", color: "#64748b" })}>Ciclo de facturación</span>
        <select
          className={cf.input}
          value={form.billingCycle}
          onChange={(e) => setForm({ ...form, billingCycle: e.target.value })}
        >
          <option value="monthly">Mensual</option>
          <option value="annual">Anual</option>
        </select>
      </label>
      {form.plan === "ENTERPRISE" && (
        <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
          <span className={css({ fontSize: "xs", color: "#64748b" })}>Agendas contratadas</span>
          <input
            type="number"
            min={1}
            className={cf.input}
            value={form.agendasCount}
            onChange={(e) => setForm({ ...form, agendasCount: Number(e.target.value) })}
          />
        </label>
      )}
      <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
        <span className={css({ fontSize: "xs", color: "#64748b" })}>
          Precio personalizado /mes (vacío = estándar)
        </span>
        <input
          type="number"
          min={0}
          placeholder="ej: 22000"
          className={cf.input}
          value={form.priceOverride}
          onChange={(e) => setForm({ ...form, priceOverride: e.target.value })}
        />
      </label>
      <label
        className={css({
          display: "flex",
          alignItems: "center",
          gap: "2",
          cursor: "pointer",
          alignSelf: "end",
          mb: "2",
        })}
      >
        <input
          type="checkbox"
          checked={form.pendingPlanPayment}
          onChange={(e) => setForm({ ...form, pendingPlanPayment: e.target.checked })}
        />
        <span className={css({ fontSize: "sm", color: "#334155" })}>Pago pendiente</span>
      </label>
      <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
        <span className={css({ fontSize: "xs", color: "#64748b" })}>Nota interna (log)</span>
        <input
          type="text"
          placeholder="Opcional"
          className={cf.input}
          value={form.note}
          onChange={(e) => setForm({ ...form, note: e.target.value })}
        />
      </label>
      <div className={css({ display: "flex", gap: "2", alignSelf: "end" })}>
        <Button type="button" variant="primary" disabled={busy} onClick={() => void save()}>
          {busy ? "Guardando…" : "Guardar"}
        </Button>
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancelar
        </Button>
      </div>
      {msg && (
        <p
          className={css({ gridColumn: "1 / -1", fontSize: "xs" })}
          style={{ color: msg.ok ? "#15803d" : "#dc2626" }}
        >
          {msg.text}
        </p>
      )}
    </div>
  );
}

// ─── Accounts table ───────────────────────────────────────────────────────────

type StatusFilter = "ALL" | RealStatus;
type PlanFilter = "ALL" | PlanKey;

function AccountsTable({
  rows,
  onUpdated,
}: {
  rows: SubscriptionRow[];
  onUpdated: () => void;
}) {
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("ALL");
  const [planFilter, setPlanFilter] = useState<PlanFilter>("ALL");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [impersonatingId, setImpersonatingId] = useState<string | null>(null);

  async function impersonate(profId: string) {
    setImpersonatingId(profId);
    try {
      const r = await fetch(`/api/admin/impersonate/${profId}`, {
        method: "POST",
        credentials: "include",
      });
      if (!r.ok) {
        const d = (await r.json().catch(() => null)) as { error?: string } | null;
        window.alert(d?.error ?? "No se pudo ingresar a la cuenta.");
        setImpersonatingId(null);
        return;
      }
      window.location.href = "/dashboard";
    } catch {
      window.alert("No se pudo ingresar a la cuenta.");
      setImpersonatingId(null);
    }
  }

  const counts = useMemo(() => {
    const s: Record<string, number> = {
      ALL: rows.length,
      paying: 0,
      on_trial: 0,
      trial_expired: 0,
      inactive: 0,
      free: 0,
    };
    const p: Record<string, number> = { FREE: 0, PRO: 0, ENTERPRISE: 0 };
    for (const r of rows) {
      s[r.realStatus]++;
      p[r.plan]++;
    }
    return { status: s, plan: p };
  }, [rows]);

  const filtered = useMemo(() => {
    let result = rows;
    const s = q.trim().toLowerCase();
    if (s) {
      result = result.filter(
        (r) =>
          r.name.toLowerCase().includes(s) ||
          (r.email?.toLowerCase().includes(s) ?? false) ||
          r.slug.toLowerCase().includes(s) ||
          (r.phone?.includes(s) ?? false),
      );
    }
    if (statusFilter !== "ALL") result = result.filter((r) => r.realStatus === statusFilter);
    if (planFilter !== "ALL") result = result.filter((r) => r.plan === planFilter);
    return result;
  }, [rows, q, statusFilter, planFilter]);

  const STATUS_TABS: { value: StatusFilter; label: string }[] = [
    { value: "ALL", label: "Todos" },
    { value: "paying", label: "Pagando" },
    { value: "on_trial", label: "En prueba" },
    { value: "trial_expired", label: "Prueba vencida" },
    { value: "inactive", label: "Inactivos" },
  ];
  const PLAN_TABS: { value: PlanFilter; label: string }[] = [
    { value: "ALL", label: "Todos los planes" },
    { value: "FREE", label: "Free" },
    { value: "PRO", label: "Pro" },
    { value: "ENTERPRISE", label: "Enterprise" },
  ];

  const thClass = css({
    py: "3",
    px: "4",
    fontWeight: "semibold",
    color: "#64748b",
    fontSize: "xs",
    whiteSpace: "nowrap",
    textAlign: "left",
  });

  return (
    <div className={css({ display: "flex", flexDir: "column", gap: "4" })}>
      {/* Search + new account */}
      <div
        className={css({
          display: "flex",
          flexWrap: "wrap",
          gap: "3",
          alignItems: "flex-end",
          justifyContent: "space-between",
        })}
      >
        <label className={css({ display: "flex", flexDir: "column", gap: "1", flex: "1", maxW: "sm" })}>
          <span className={css({ fontSize: "xs", fontWeight: "medium", color: "#64748b" })}>
            Buscar
          </span>
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Nombre, email o slug…"
            autoComplete="off"
            className={css({
              px: "3",
              py: "2.5",
              rounded: "lg",
              borderWidth: "1px",
              borderColor: "#e2e8f0",
              bg: "white",
              fontSize: "sm",
              color: "#0f172a",
              _focusVisible: {
                outline: "2px solid",
                outlineColor: "#93c5fd",
                outlineOffset: "2px",
              },
            })}
          />
        </label>
        <Link
          to="/admin/professionals/new"
          className={css({
            display: "inline-flex",
            alignItems: "center",
            px: "4",
            py: "2.5",
            rounded: "lg",
            fontSize: "sm",
            fontWeight: "semibold",
            color: "white",
            bg: "linear-gradient(90deg, #4f46e5, #6366f1)",
            boxShadow: "sm",
            _hover: { opacity: 0.95 },
            whiteSpace: "nowrap",
          })}
        >
          Nueva cuenta
        </Link>
      </div>

      {/* Status filter tabs */}
      <div className={css({ display: "flex", gap: "2", flexWrap: "wrap" })}>
        {STATUS_TABS.map((t) => (
          <FilterPill
            key={t.value}
            active={statusFilter === t.value}
            label={t.label}
            count={counts.status[t.value]}
            onClick={() => {
              setStatusFilter(t.value);
              setEditingId(null);
            }}
          />
        ))}
        <span className={css({ w: "px", h: "6", bg: "#e2e8f0", alignSelf: "center", display: { base: "none", sm: "block" } })} />
        {PLAN_TABS.map((t) => (
          <FilterPill
            key={t.value}
            active={planFilter === t.value}
            label={t.label}
            count={t.value !== "ALL" ? counts.plan[t.value] : undefined}
            onClick={() => {
              setPlanFilter(t.value);
              setEditingId(null);
            }}
          />
        ))}
      </div>

      {/* Table */}
      <div
        className={css({
          overflowX: "auto",
          rounded: "xl",
          borderWidth: "1px",
          borderColor: "#e2e8f0",
          bg: "white",
        })}
      >
        <table className={css({ w: "full", borderCollapse: "collapse", fontSize: "sm" })}>
          <thead>
            <tr className={css({ bg: "#f8fafc", borderBottomWidth: "1px", borderColor: "#e2e8f0" })}>
              <th className={thClass}>Profesional</th>
              <th className={cx(thClass, css({ display: { base: "none", lg: "table-cell" } }))}>
                Rubro
              </th>
              <th className={thClass}>Plan</th>
              <th className={thClass}>Estado</th>
              <th className={cx(thClass, css({ display: { base: "none", sm: "table-cell" } }))}>
                Prueba
              </th>
              <th className={cx(thClass, css({ display: { base: "none", md: "table-cell" } }))}>
                MP
              </th>
              <th className={cx(thClass, css({ display: { base: "none", md: "table-cell" } }))}>
                Google
              </th>
              <th className={cx(thClass, css({ display: { base: "none", sm: "table-cell" } }))}>
                Agendas
              </th>
              <th className={cx(thClass, css({ display: { base: "none", md: "table-cell" } }))}>
                Turnos
              </th>
              <th className={cx(thClass, css({ display: { base: "none", lg: "table-cell" } }))}>
                Alta
              </th>
              <th className={thClass} />
            </tr>
          </thead>
          <tbody>
            {filtered.map((row) => {
              const isEditing = editingId === row.id;
              const planColor = PLAN_COLORS[row.plan] ?? "#64748b";

              const trialLabel =
                row.trialDaysLeft === null
                  ? null
                  : row.trialDaysLeft <= 0
                    ? "Vencida"
                    : row.trialDaysLeft === 1
                      ? "1 día"
                      : `${row.trialDaysLeft} días`;
              const trialExpired = row.trialDaysLeft !== null && row.trialDaysLeft <= 0;
              const trialWarning =
                row.trialDaysLeft !== null && row.trialDaysLeft > 0 && row.trialDaysLeft <= 5;

              return (
                <Fragment key={row.id}>
                  <tr
                    className={css({
                      borderTopWidth: "1px",
                      borderColor: "#f1f5f9",
                      _hover: { bg: "#fafafa" },
                      verticalAlign: "top",
                    })}
                  >
                    {/* Profesional */}
                    <td className={css({ py: "3", px: "4" })}>
                      <div className={css({ display: "flex", flexDir: "column", gap: "0.5" })}>
                        <Link
                          to={`/admin/professionals/${row.id}`}
                          className={css({
                            fontWeight: "semibold",
                            color: "#1d4ed8",
                            _hover: { textDecoration: "underline" },
                            fontSize: "sm",
                          })}
                        >
                          {row.name}
                        </Link>
                        <span className={css({ fontSize: "xs", color: "#94a3b8" })}>
                          {row.email ?? "—"}
                        </span>
                        {row.phone ? (
                          <span className={css({ fontSize: "xs", color: "#94a3b8" })}>
                            {row.phone}
                          </span>
                        ) : null}
                        {row.pendingPlanPayment && (
                          <span
                            className={css({
                              fontSize: "xs",
                              bg: "#fef3c7",
                              color: "#92400e",
                              px: "1.5",
                              py: "0.5",
                              rounded: "md",
                              display: "inline-block",
                              fontWeight: "medium",
                              mt: "0.5",
                            })}
                          >
                            Pago pendiente
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Rubro */}
                    <td
                      className={cx(
                        css({ py: "3", px: "4", color: "#64748b", fontSize: "xs" }),
                        css({ display: { base: "none", lg: "table-cell" } }),
                      )}
                    >
                      {row.category ?? "—"}
                      {row.specialty && (
                        <p className={css({ fontStyle: "italic", mt: "0.5", color: "#94a3b8" })}>
                          {row.specialty}
                        </p>
                      )}
                    </td>

                    {/* Plan */}
                    <td className={css({ py: "3", px: "4" })}>
                      <div className={css({ display: "flex", alignItems: "center", gap: "1.5" })}>
                        <span
                          className={css({ w: "2", h: "2", rounded: "full", flexShrink: 0 })}
                          style={{ background: planColor }}
                        />
                        <span
                          className={css({
                            fontSize: "xs",
                            fontWeight: "semibold",
                            color: "#334155",
                          })}
                        >
                          {PLAN_LABEL[row.plan]}
                        </span>
                      </div>
                      {row.plan !== "FREE" && row.monthlyRate > 0 && (
                        <span className={css({ fontSize: "xs", color: "#94a3b8", mt: "0.5", display: "block" })}>
                          {formatARS(row.monthlyRate)}/mes
                          {row.priceOverride != null && (
                            <span className={css({ color: "#7c3aed", ml: "1" })}>*</span>
                          )}
                        </span>
                      )}
                    </td>

                    {/* Estado */}
                    <td className={css({ py: "3", px: "4" })}>
                      <AccountStatusBadge status={row.realStatus} />
                    </td>

                    {/* Prueba */}
                    <td
                      className={cx(
                        css({ py: "3", px: "4", whiteSpace: "nowrap" }),
                        css({ display: { base: "none", sm: "table-cell" } }),
                      )}
                    >
                      {trialLabel ? (
                        <span
                          className={css({
                            display: "inline-block",
                            px: "2",
                            py: "0.5",
                            rounded: "md",
                            fontSize: "xs",
                            fontWeight: "semibold",
                          })}
                          style={{
                            background: trialExpired
                              ? "#fee2e2"
                              : trialWarning
                                ? "#fef9c3"
                                : "#dcfce7",
                            color: trialExpired
                              ? "#b91c1c"
                              : trialWarning
                                ? "#854d0e"
                                : "#166534",
                          }}
                        >
                          {trialLabel}
                        </span>
                      ) : (
                        <span className={css({ color: "#cbd5e1", fontSize: "xs" })}>—</span>
                      )}
                    </td>

                    {/* MP */}
                    <td
                      className={cx(
                        css({ py: "3", px: "4" }),
                        css({ display: { base: "none", md: "table-cell" } }),
                      )}
                    >
                      <span
                        className={css({
                          display: "inline-block",
                          px: "2",
                          py: "0.5",
                          rounded: "md",
                          fontSize: "xs",
                          fontWeight: "medium",
                          bg: row.mpConnected ? "#dcfce7" : "#f1f5f9",
                          color: row.mpConnected ? "#15803d" : "#94a3b8",
                        })}
                      >
                        {row.mpConnected ? "Conectado" : "Sin MP"}
                      </span>
                    </td>

                    {/* Google */}
                    <td
                      className={cx(
                        css({ py: "3", px: "4" }),
                        css({ display: { base: "none", md: "table-cell" } }),
                      )}
                    >
                      <span
                        className={css({
                          display: "inline-block",
                          px: "2",
                          py: "0.5",
                          rounded: "md",
                          fontSize: "xs",
                          fontWeight: "medium",
                          bg: row.googleConnected ? "#dcfce7" : "#f1f5f9",
                          color: row.googleConnected ? "#15803d" : "#94a3b8",
                        })}
                      >
                        {row.googleConnected ? "Conectado" : "Sin Google"}
                      </span>
                    </td>

                    {/* Agendas */}
                    <td
                      className={cx(
                        css({
                          py: "3",
                          px: "4",
                          fontSize: "sm",
                          fontWeight: "semibold",
                          color: "#334155",
                        }),
                        css({ display: { base: "none", sm: "table-cell" } }),
                      )}
                    >
                      {row.agendasCount}
                    </td>

                    {/* Turnos */}
                    <td
                      className={cx(
                        css({
                          py: "3",
                          px: "4",
                          fontSize: "sm",
                          fontWeight: "semibold",
                          color: "#334155",
                        }),
                        css({ display: { base: "none", md: "table-cell" } }),
                      )}
                    >
                      {row.appointmentsCount}
                    </td>

                    {/* Alta */}
                    <td
                      className={cx(
                        css({ py: "3", px: "4", color: "#94a3b8", fontSize: "xs", whiteSpace: "nowrap" }),
                        css({ display: { base: "none", lg: "table-cell" } }),
                      )}
                    >
                      {format(new Date(row.createdAt), "d MMM yyyy", { locale: es })}
                    </td>

                    {/* Edit */}
                    <td className={css({ py: "3", px: "4", whiteSpace: "nowrap" })}>
                      <div className={css({ display: "flex", gap: "2" })}>
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          onClick={() => setEditingId(isEditing ? null : row.id)}
                        >
                          {isEditing ? "Cerrar" : "Editar"}
                        </Button>
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          disabled={impersonatingId === row.id}
                          onClick={() => void impersonate(row.id)}
                        >
                          {impersonatingId === row.id ? "Ingresando…" : "Ingresar como"}
                        </Button>
                      </div>
                    </td>
                  </tr>

                  {/* Inline editor row */}
                  {isEditing && (
                    <tr
                      className={css({ bg: "#fafafa", borderTopWidth: "1px", borderColor: "#f1f5f9" })}
                    >
                      <td colSpan={11} className={css({ px: "4", pb: "4" })}>
                        <SubscriptionEditor
                          row={row}
                          onSaved={() => {
                            setEditingId(null);
                            onUpdated();
                          }}
                          onCancel={() => setEditingId(null)}
                        />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>

        {filtered.length === 0 && (
          <p
            className={css({
              py: "8",
              textAlign: "center",
              color: "#94a3b8",
              fontSize: "sm",
            })}
          >
            {q.trim() ? `Sin resultados para "${q.trim()}".` : "Sin cuentas en este filtro."}
          </p>
        )}
      </div>

      <p className={css({ fontSize: "xs", color: "#94a3b8" })}>
        Mostrando {filtered.length} de {rows.length} cuentas principales.
      </p>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function AdminPlanes() {
  const [pricingData, setPricingData] = useState<PricingData | null>(null);
  const [adminData, setAdminData] = useState<AdminData | null>(null);
  const [annualEnabled, setAnnualEnabled] = useState(true);
  const [annualBusy, setAnnualBusy] = useState(false);
  const [pricingOpen, setPricingOpen] = useState(false);

  const loadPricing = useCallback(async () => {
    const r = await fetch("/api/admin/plan-pricing", { credentials: "include" });
    if (r.ok) {
      const d = (await r.json()) as PricingData;
      setPricingData(d);
      setAnnualEnabled(d.annualBillingEnabled);
    }
  }, []);

  const loadAdmin = useCallback(async () => {
    const r = await fetch("/api/admin/subscriptions", { credentials: "include" });
    if (r.ok) setAdminData((await r.json()) as AdminData);
  }, []);

  useEffect(() => {
    void Promise.all([loadPricing(), loadAdmin()]);
  }, [loadPricing, loadAdmin]);

  async function toggleAnnual(enabled: boolean) {
    setAnnualBusy(true);
    await fetch("/api/admin/plan-pricing/settings", {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ annualBillingEnabled: enabled }),
    });
    setAnnualBusy(false);
    setAnnualEnabled(enabled);
  }

  const profs = adminData?.professionals ?? [];
  const mrr = adminData?.mrr;

  const payingCount = profs.filter((p) => p.realStatus === "paying").length;
  const trialCount = profs.filter((p) => p.realStatus === "on_trial").length;
  const expiredCount = profs.filter((p) => p.realStatus === "trial_expired").length;

  return (
    <AdminPageShell
      title="Planes y cuentas"
      description="MRR real, estado de cada cuenta, suscripciones y precios de planes."
    >
      {/* ── MRR Cards ────────────────────────────────── */}
      <div
        className={css({
          display: "grid",
          gridTemplateColumns: { base: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" },
          gap: "4",
        })}
      >
        <MrrCard
          label="MRR Real"
          value={mrr ? formatARS(mrr.real) : "—"}
          sub="Cuentas con pago confirmado, sin trial"
          accent="#16a34a"
        />
        <MrrCard
          label="MRR Potencial"
          value={mrr ? formatARS(mrr.potential) : "—"}
          sub="Si los trials vencidos convirtieran"
          accent="#4338CA"
        />
        <CountCard
          label="Pagando"
          count={payingCount}
          dot="#16a34a"
          sub="Plan activo + pago confirmado"
        />
        <CountCard
          label="En prueba / Vencida"
          count={trialCount + expiredCount}
          dot={expiredCount > 0 ? "#dc2626" : "#ca8a04"}
          sub={`${trialCount} en prueba · ${expiredCount} vencidas`}
        />
      </div>

      {/* ── Accounts table ───────────────────────────── */}
      <div>
        <h2
          className={css({
            fontSize: "lg",
            fontWeight: "semibold",
            color: "#0f172a",
            mb: "4",
          })}
        >
          Cuentas
        </h2>
        {adminData ? (
          <AccountsTable rows={profs} onUpdated={() => void loadAdmin()} />
        ) : (
          <p className={css({ color: "#94a3b8", fontSize: "sm" })}>Cargando cuentas…</p>
        )}
      </div>

      {/* ── Plan Pricing (collapsible) ────────────────── */}
      <div className={cx(cf.dashSurface, css({ overflow: "hidden" }))}>
        <button
          type="button"
          onClick={() => setPricingOpen((v) => !v)}
          className={css({
            w: "full",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: "5",
            cursor: "pointer",
            _hover: { bg: "#fafafa" },
            transition: "background 0.15s",
          })}
        >
          <div className={css({ textAlign: "left" })}>
            <p className={css({ fontWeight: "semibold", color: "#0f172a", fontSize: "sm" })}>
              Precios de planes
            </p>
            <p className={css({ fontSize: "xs", color: "#64748b", mt: "0.5" })}>
              Los precios se muestran en la landing y calculadora.
            </p>
          </div>
          <div
            className={css({
              display: "flex",
              alignItems: "center",
              gap: "3",
              flexShrink: 0,
            })}
          >
            {/* Annual billing toggle */}
            <label
              className={css({ display: "flex", alignItems: "center", gap: "2", cursor: "pointer" })}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                role="switch"
                aria-checked={annualEnabled}
                onClick={() => !annualBusy && void toggleAnnual(!annualEnabled)}
                className={css({
                  position: "relative",
                  w: "9",
                  h: "5",
                  rounded: "full",
                  cursor: annualBusy ? "not-allowed" : "pointer",
                  transition: "background 0.2s ease",
                })}
                style={{ background: annualEnabled ? "#4338CA" : "#e2e8f0" }}
              >
                <span
                  className={css({
                    position: "absolute",
                    top: "0.5",
                    w: "4",
                    h: "4",
                    rounded: "full",
                    bg: "white",
                    transition: "left 0.2s ease",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
                  })}
                  style={{ left: annualEnabled ? "calc(100% - 18px)" : "2px" }}
                />
              </div>
              <span className={css({ fontSize: "xs", color: "#64748b", whiteSpace: "nowrap" })}>
                Facturación anual
              </span>
            </label>

            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              className={css({ transition: "transform 0.2s", color: "#94a3b8" })}
              style={{ transform: pricingOpen ? "rotate(180deg)" : "rotate(0deg)" }}
            >
              <path
                d="M4 6l4 4 4-4"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </button>

        {pricingOpen && (
          <div
            className={css({
              px: "5",
              pb: "5",
              borderTopWidth: "1px",
              borderColor: "#f1f5f9",
            })}
          >
            <div className={css({ pt: "5" })}>
              {pricingData ? (
                <PlanPriceEditor pricing={pricingData.pricing} onSaved={() => void loadPricing()} />
              ) : (
                <p className={css({ color: "#94a3b8", fontSize: "sm" })}>Cargando precios…</p>
              )}
            </div>
          </div>
        )}
      </div>
    </AdminPageShell>
  );
}
